import { useState, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../AuthContext'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import {
  getTask,
  updateTask,
  deleteTask,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  uploadTaskAttachments,
  deleteTaskAttachment,
  getTaskAttachmentViewUrl,
  getTaskAttachmentDownloadUrl,
  getPotentialRelations,
  addTaskRelation,
  deleteTaskRelation,
  getTasksUsers,
  getTaskComments,
  createTaskComment,
  deleteTaskComment,
  updateTaskComment,
  uploadTaskCommentAttachments,
  getTaskCommentAttachmentViewUrl,
  getTaskCommentAttachmentDownloadUrl,
  createTask as apiCreateTask,
  TaskItem,
  TaskStatus,
  TaskCommentItem
} from '../../api/tasks'
import MarkdownToolbar from './MarkdownToolbar'
import { 
  ArrowLeft, Calendar, Paperclip, CheckSquare, Trash2, Edit2, Check, X, 
  UploadCloud, Link2, Plus, AlertCircle, FileText, Send, User, MessageSquare,
  ChevronRight, Sparkles, Image, ListTodo
} from 'lucide-react'

const formatDateTime = (value?: string | null) => {
  if (!value) return null
  return new Date(value).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const isOverdue = (deadline?: string | null, status?: TaskStatus) => {
  if (!deadline || status === 'completed') return false
  const deadlineDate = new Date(deadline)
  deadlineDate.setHours(23, 59, 59, 999)
  return deadlineDate < new Date()
}

const getFileSizeString = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const isImageFile = (filename: string, mime?: string): boolean => {
  if (mime && mime.startsWith('image/')) return true
  const ext = filename.split('.').pop()?.toLowerCase()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')
}

export default function TaskViewPage() {
  const { projectId, taskId } = useParams<{ projectId: string; taskId: string }>()
  const taskIdNum = Number(taskId)
  const projectIdNum = Number(projectId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)
  const descTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const commentTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const commentFileInputRef = useRef<HTMLInputElement>(null)
  const editCommentTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Auth check using AuthContext
  const { user: currentUserObj } = useAuth()

  // Edit / Input states
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [isEditingDesc, setIsEditingDesc] = useState(false)
  const [editDesc, setEditDesc] = useState('')
  const [newChecklistItemText, setNewChecklistItemText] = useState('')
  
  // Relations/Subtasks modals
  const [showRelationModal, setShowRelationModal] = useState(false)
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(false)
  
  // Subtask form state
  const [subtaskTitle, setSubtaskTitle] = useState('')
  const [subtaskDesc, setSubtaskDesc] = useState('')
  const [subtaskDeadline, setSubtaskDeadline] = useState('')
  const [subtaskAssignees, setSubtaskAssignees] = useState<number[]>([])

  // Upload/Comments states
  const [isUploading, setIsUploading] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [commentFiles, setCommentFiles] = useState<File[]>([])
  const [isPostingComment, setIsPostingComment] = useState(false)

  // Inline editing states
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null)
  const [editingCommentText, setEditingCommentText] = useState('')
  const [editingChecklistItemId, setEditingChecklistItemId] = useState<number | null>(null)
  const [editingChecklistItemText, setEditingChecklistItemText] = useState('')

  // Queries
  const { data: task, isLoading, error } = useQuery({
    queryKey: ['task', taskIdNum],
    queryFn: () => getTask(taskIdNum),
    enabled: !!taskIdNum,
  })

  const { data: comments = [], isLoading: commentsLoading } = useQuery({
    queryKey: ['taskComments', taskIdNum],
    queryFn: () => getTaskComments(taskIdNum),
    enabled: !!taskIdNum,
  })

  const { data: potentialRelations = [] } = useQuery({
    queryKey: ['potentialRelations', taskIdNum],
    queryFn: () => getPotentialRelations(taskIdNum),
    enabled: showRelationModal && !!taskIdNum,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['tasksUsers'],
    queryFn: getTasksUsers,
  })

  // Mutations
  const updateTaskMutation = useMutation({
    mutationFn: (data: Partial<Omit<TaskItem, 'assignee_ids'>> & { assignee_ids?: number[] }) => updateTask(taskIdNum, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка обновления задачи: ${err.message}`)
    }
  })

  const deleteTaskMutation = useMutation({
    mutationFn: () => deleteTask(taskIdNum),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', projectIdNum] })
      navigate(`/projects/${projectId}/tasks`)
    },
    onError: (err) => {
      alert(`Ошибка удаления задачи: ${err.message}`)
    }
  })

  const addChecklistItemMutation = useMutation({
    mutationFn: (title: string) => addChecklistItem(taskIdNum, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
      setNewChecklistItemText('')
    },
    onError: (err) => {
      alert(`Ошибка чек-листа: ${err.message}`)
    }
  })

  const updateChecklistItemMutation = useMutation({
    mutationFn: ({ id, title, is_completed }: { id: number; title?: string; is_completed?: boolean }) => 
      updateChecklistItem(id, { title, is_completed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка: ${err.message}`)
    }
  })

  const deleteChecklistItemMutation = useMutation({
    mutationFn: deleteChecklistItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка: ${err.message}`)
    }
  })

  const createSubtaskMutation = useMutation({
    mutationFn: apiCreateTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
      queryClient.invalidateQueries({ queryKey: ['tasks', projectIdNum] })
      setShowAddSubtaskModal(false)
      setSubtaskTitle('')
      setSubtaskDesc('')
      setSubtaskDeadline('')
      setSubtaskAssignees([])
    },
    onError: (err) => {
      alert(`Ошибка подзадачи: ${err.message}`)
    }
  })

  const addRelationMutation = useMutation({
    mutationFn: (relatedId: number) => addTaskRelation(taskIdNum, relatedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
      queryClient.invalidateQueries({ queryKey: ['potentialRelations', taskIdNum] })
      setShowRelationModal(false)
    },
    onError: (err) => {
      alert(`Ошибка связи: ${err.message}`)
    }
  })

  const deleteRelationMutation = useMutation({
    mutationFn: (relatedId: number) => deleteTaskRelation(taskIdNum, relatedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка удаления связи: ${err.message}`)
    }
  })

  const deleteAttachmentMutation = useMutation({
    mutationFn: deleteTaskAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка удаления файла: ${err.message}`)
    }
  })

  const deleteCommentMutation = useMutation({
    mutationFn: deleteTaskComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', taskIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка удаления комментария: ${err.message}`)
    }
  })

  const updateCommentMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) => updateTaskComment(id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', taskIdNum] })
      setEditingCommentId(null)
      setEditingCommentText('')
    },
    onError: (err) => {
      alert(`Ошибка редактирования отчета: ${err.message}`)
    }
  })

  // Action Handlers
  const handleSaveCommentEdit = (commentId: number) => {
    if (!editingCommentText.trim()) return
    updateCommentMutation.mutate({ id: commentId, content: editingCommentText.trim() })
  }

  const handleSaveChecklistItemTitle = (itemId: number) => {
    if (!editingChecklistItemText.trim()) return
    updateChecklistItemMutation.mutate({ id: itemId, title: editingChecklistItemText.trim() })
    setEditingChecklistItemId(null)
  }
  const handleSaveTitle = () => {
    if (!editTitle.trim()) return
    updateTaskMutation.mutate({ title: editTitle.trim() })
    setIsEditingTitle(false)
  }

  const handleSaveDesc = () => {
    updateTaskMutation.mutate({ description: editDesc.trim() || null })
    setIsEditingDesc(false)
  }

  const handleStatusChange = (status: TaskStatus) => {
    updateTaskMutation.mutate({ status })
  }

  const handleDeadlineChange = (deadline: string) => {
    updateTaskMutation.mutate({ deadline: deadline || null })
  }

  const handleToggleAssignee = (userId: number) => {
    const currentIds = task?.assignees?.map(u => u.id) || []
    const nextIds = currentIds.includes(userId)
      ? currentIds.filter(id => id !== userId)
      : [...currentIds, userId]
    
    updateTaskMutation.mutate({ assignee_ids: nextIds })
  }

  const handleAddChecklistItem = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newChecklistItemText.trim()) return
    addChecklistItemMutation.mutate(newChecklistItemText.trim())
  }

  const handleCreateSubtask = (e: React.FormEvent) => {
    e.preventDefault()
    if (!subtaskTitle.trim()) return
    createSubtaskMutation.mutate({
      project_id: projectIdNum,
      title: subtaskTitle.trim(),
      description: subtaskDesc.trim() || undefined,
      status: 'new',
      deadline: subtaskDeadline || null,
      assignee_ids: subtaskAssignees,
      parent_id: taskIdNum
    })
  }

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return
    setIsUploading(true)
    try {
      await uploadTaskAttachments(taskIdNum, files)
      queryClient.invalidateQueries({ queryKey: ['task', taskIdNum] })
    } catch (err) {
      alert(`Ошибка загрузки файлов: ${err instanceof Error ? err.message : 'неизвестная ошибка'}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handlePostCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim() && commentFiles.length === 0) return
    
    setIsPostingComment(true)
    try {
      // Create comment
      const result = await createTaskComment(taskIdNum, commentText.trim() || 'Отчет')
      
      // Upload files to comment if any
      if (commentFiles.length > 0) {
        await uploadTaskCommentAttachments(result.id, commentFiles)
      }
      
      setCommentText('')
      setCommentFiles([])
      queryClient.invalidateQueries({ queryKey: ['taskComments', taskIdNum] })
    } catch (err) {
      alert(`Ошибка при отправке отчета/комментария: ${err instanceof Error ? err.message : 'неизвестная ошибка'}`)
    } finally {
      setIsPostingComment(false)
    }
  }

  const handleCommentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setCommentFiles(prev => [...prev, ...Array.from(e.target.files!)])
    }
  }

  const handleRemoveCommentFile = (index: number) => {
    setCommentFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleCommentDelete = (commentId: number) => {
    if (confirm('Удалить отчет/комментарий?')) {
      deleteCommentMutation.mutate(commentId)
    }
  }

  const handleDeleteTask = () => {
    if (confirm('Удалить эту задачу? Все чек-листы, связи и файлы будут удалены.')) {
      deleteTaskMutation.mutate()
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500 font-medium">Загрузка задачи...</div>
  }

  if (error || !task) {
    return (
      <div className="text-center py-12 space-y-4">
        <div className="text-red-500 font-bold text-lg">Задача не найдена</div>
        <Link to={`/projects/${projectId}/tasks`} className="text-blue-600 hover:underline">
          Вернуться к списку задач
        </Link>
      </div>
    )
  }

  const overdue = isOverdue(task.deadline, task.status)
  const checklistTotal = task.checklist ? task.checklist.length : 0
  const checklistCompleted = task.checklist ? task.checklist.filter(c => c.is_completed).length : 0
  const checklistPercent = checklistTotal ? Math.round((checklistCompleted / checklistTotal) * 100) : 0

  return (
    <div className="max-w-6xl mx-auto py-6 px-4 space-y-8">
      {/* Breadcrumbs / Back */}
      <div className="flex items-center justify-between">
        <Link
          to={`/projects/${projectId}/tasks`}
          className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 gap-1.5"
        >
          <ArrowLeft className="w-4 h-4" />
          К списку задач
        </Link>
        
        <button
          onClick={handleDeleteTask}
          className="inline-flex items-center text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1.5 rounded-lg border border-red-200 transition gap-1.5"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Удалить задачу
        </button>
      </div>

      {/* Main Grid: Left for task text (blog-like) and comments, Right for attributes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Main Task Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 space-y-6">
            
            {/* Parent Breadcrumb if exists */}
            {task.parent_id && (
              <div className="flex items-center text-xs text-gray-400 gap-1 select-none">
                <span>Родительская задача:</span>
                <Link 
                  to={`/projects/${projectId}/tasks/${task.parent_id}`}
                  className="font-semibold text-blue-500 hover:underline"
                >
                  {task.parent_title}
                </Link>
                <ChevronRight className="w-3 h-3 text-gray-300" />
              </div>
            )}

            {/* Editable Title */}
            <div>
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="flex-grow font-bold text-2xl px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <button 
                    onClick={handleSaveTitle}
                    className="p-2.5 text-white bg-green-600 hover:bg-green-700 rounded-lg shrink-0"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => setIsEditingTitle(false)}
                    className="p-2.5 text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="group flex items-start justify-between gap-4">
                  <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
                    {task.title}
                  </h1>
                  <button
                    onClick={() => {
                      setEditTitle(task.title)
                      setIsEditingTitle(true)
                    }}
                    className="text-gray-400 hover:text-blue-600 p-1.5 hover:bg-blue-50 rounded-lg transition shrink-0"
                    title="Редактировать название"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="text-xs text-gray-400 mt-2 flex items-center gap-3">
                <span>Создана: <strong>{formatDateTime(task.created_at)}</strong></span>
                {task.creator_name && (
                  <span>Автор: <strong className="text-gray-500">{task.creator_name}</strong></span>
                )}
              </div>
            </div>

            {/* Description (Markdown editor & viewer) */}
            <div className="border-t border-gray-100 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 text-lg">📝 Описание задачи</h3>
                {!isEditingDesc && (
                  <button
                    onClick={() => {
                      setEditDesc(task.description || '')
                      setIsEditingDesc(true)
                    }}
                    className="inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded gap-1 transition"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Редактировать
                  </button>
                )}
              </div>

              {isEditingDesc ? (
                <div className="space-y-3">
                  <MarkdownToolbar
                    textareaRef={descTextareaRef}
                    value={editDesc}
                    onChange={setEditDesc}
                    users={users}
                  />
                  <textarea
                    ref={descTextareaRef}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="Подробно распишите задачу..."
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-b-lg border-t-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-sans"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveDesc}
                      className="px-4 py-2 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                    >
                      Сохранить
                    </button>
                    <button
                      onClick={() => setIsEditingDesc(false)}
                      className="px-4 py-2 text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              ) : task.description ? (
                <div className="prose prose-sm max-w-none bg-gray-50 border border-gray-100 rounded-xl p-5 text-gray-800 leading-relaxed">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    components={{
                      a: ({ href, children }) => (
                        <a 
                          href={href} 
                          className="text-blue-600 hover:text-blue-850 hover:underline font-semibold break-all" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      )
                    }}
                  >
                    {task.description}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-gray-400 italic text-sm">Описание задачи отсутствует.</p>
              )}
            </div>

            {/* Checklist */}
            <div className="border-t border-gray-100 pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-blue-500" />
                  Чек-лист (подзадачи)
                </h3>
                {checklistTotal > 0 && (
                  <span className="text-xs font-bold text-gray-500">
                    {checklistCompleted} из {checklistTotal} ({checklistPercent}%)
                  </span>
                )}
              </div>

              {checklistTotal > 0 && (
                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${checklistPercent}%` }}
                  ></div>
                </div>
              )}

              <div className="space-y-2">
                {task.checklist && task.checklist.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {task.checklist.map((item) => {
                      const isItemEditing = editingChecklistItemId === item.id;
                      
                      if (isItemEditing) {
                        return (
                          <div 
                            key={item.id} 
                            className="flex items-center justify-between py-2 gap-3 w-full"
                          >
                            <div className="flex items-center gap-2 flex-grow">
                              <input
                                type="text"
                                value={editingChecklistItemText}
                                onChange={(e) => setEditingChecklistItemText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveChecklistItemTitle(item.id)
                                  } else if (e.key === 'Escape') {
                                    setEditingChecklistItemId(null)
                                  }
                                }}
                                className="flex-grow text-sm px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => handleSaveChecklistItemTitle(item.id)}
                                className="p-1.5 text-white bg-green-600 hover:bg-green-700 rounded-lg transition"
                                title="Сохранить"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingChecklistItemId(null)}
                                className="p-1.5 text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                                title="Отмена"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )
                      }
                      
                      return (
                        <div 
                          key={item.id} 
                          className="flex items-center justify-between py-2 group gap-3"
                        >
                          <label 
                            className="flex items-start gap-3 cursor-pointer flex-grow select-none"
                            onDoubleClick={() => {
                              setEditingChecklistItemId(item.id)
                              setEditingChecklistItemText(item.title)
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.is_completed}
                              onChange={(e) => {
                                updateChecklistItemMutation.mutate({
                                  id: item.id,
                                  is_completed: e.target.checked
                                })
                              }}
                              className="mt-1 rounded text-blue-600 focus:ring-blue-500 w-4.5 h-4.5"
                            />
                            <span className={`text-sm ${
                              item.is_completed ? 'line-through text-gray-400' : 'text-gray-700'
                            }`}>
                              {item.title}
                            </span>
                          </label>
                          
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition duration-150 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingChecklistItemId(item.id)
                                setEditingChecklistItemText(item.title)
                              }}
                              className="text-gray-400 hover:text-blue-600 p-1 rounded"
                              title="Редактировать пункт"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteChecklistItemMutation.mutate(item.id)}
                              className="text-gray-400 hover:text-red-600 p-1 rounded"
                              title="Удалить пункт"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 italic text-sm py-2">Чек-лист не заполнен.</p>
                )}

                <form onSubmit={handleAddChecklistItem} className="flex gap-2 mt-3">
                  <input
                    type="text"
                    placeholder="Добавить новый пункт..."
                    value={newChecklistItemText}
                    onChange={(e) => setNewChecklistItemText(e.target.value)}
                    className="flex-grow text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    type="submit"
                    disabled={addChecklistItemMutation.isPending || !newChecklistItemText.trim()}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-xs px-3.5 rounded-lg flex items-center justify-center gap-1 transition"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Добавить
                  </button>
                </form>
              </div>
            </div>

            {/* Hierarchical Subtasks Section */}
            <div className="border-t border-gray-100 pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                  <ListTodo className="w-5 h-5 text-indigo-500" />
                  Подзадачи
                </h3>
                <button
                  onClick={() => setShowAddSubtaskModal(true)}
                  className="inline-flex items-center text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 rounded gap-1 transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Создать подзадачу
                </button>
              </div>

              {task.subtasks && task.subtasks.length > 0 ? (
                <div className="border border-gray-150 rounded-xl divide-y divide-gray-100 overflow-hidden bg-gray-50/20">
                  {task.subtasks.map((sub) => (
                    <div 
                      key={sub.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 transition"
                    >
                      <div className="flex items-center gap-2 overflow-hidden mr-2">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          sub.status === 'completed' 
                            ? 'bg-emerald-500' 
                            : sub.status === 'in_progress'
                            ? 'bg-amber-500'
                            : 'bg-blue-500'
                        }`}></span>
                        <Link 
                          to={`/projects/${projectId}/tasks/${sub.id}`}
                          className="text-sm font-semibold text-gray-800 hover:text-blue-600 hover:underline truncate"
                        >
                          {sub.title}
                        </Link>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {sub.deadline && (
                          <span className="text-[10px] text-gray-400 font-mono">
                            до {new Date(sub.deadline).toLocaleDateString('ru-RU')}
                          </span>
                        )}
                        <Link 
                          to={`/projects/${projectId}/tasks/${sub.id}`}
                          className="text-xs bg-white text-gray-600 border border-gray-250 hover:bg-gray-100 px-2 py-1 rounded transition"
                        >
                          Открыть
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 italic text-sm">У этой задачи нет подзадач.</p>
              )}
            </div>

          </div>

          {/* Comments Section (JIRA/Bitrix style comments & files) */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 space-y-6">
            <h3 className="font-bold text-gray-900 text-xl border-b border-gray-100 pb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-500" />
              Отчеты и обсуждение ({comments.length})
            </h3>

            {/* List of Comments */}
            {commentsLoading ? (
              <div className="text-gray-400 text-sm py-4 italic">Загрузка обсуждения...</div>
            ) : comments.length > 0 ? (
              <div className="space-y-6">
                {comments.map((comment: TaskCommentItem) => {
                  const isCommentAuthor = currentUserObj && (Number(comment.user_id) === Number(currentUserObj.id));

                  return (
                    <div key={comment.id} className="border border-gray-150 rounded-xl p-4 space-y-3 bg-gray-50/20">
                      {/* Comment Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm shrink-0">
                            {comment.user_name ? comment.user_name[0].toUpperCase() : 'U'}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-800">{comment.user_name || 'Пользователь'}</div>
                            <div className="text-[10px] text-gray-400">{formatDateTime(comment.created_at)}</div>
                          </div>
                        </div>

                        {/* Comment actions (edit/delete) */}
                        {isCommentAuthor && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingCommentId(comment.id)
                                setEditingCommentText(comment.content)
                              }}
                              className="text-gray-400 hover:text-blue-600 transition p-1"
                              title="Редактировать отчет"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCommentDelete(comment.id)}
                              className="text-gray-400 hover:text-red-600 transition p-1"
                              title="Удалить отчет"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Comment text (Markdown enabled or Edit Mode) */}
                      {editingCommentId === comment.id ? (
                        <div className="space-y-3 pt-1">
                          <MarkdownToolbar
                            textareaRef={editCommentTextareaRef}
                            value={editingCommentText}
                            onChange={setEditingCommentText}
                            users={users}
                          />
                          <textarea
                            ref={editCommentTextareaRef}
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            placeholder="Отредактируйте отчет/комментарий..."
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-b-lg border-t-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-sans bg-white"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleSaveCommentEdit(comment.id)}
                              disabled={updateCommentMutation.isPending || !editingCommentText.trim()}
                              className="px-3.5 py-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                            >
                              Сохранить
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingCommentId(null)}
                              className="px-3.5 py-1.5 text-xs font-bold bg-gray-100 hover:bg-gray-250 text-gray-600 rounded-lg transition"
                            >
                              Отмена
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed pl-1">
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm, remarkBreaks]}
                            components={{
                              a: ({ href, children }) => (
                                <a 
                                  href={href} 
                                  className="text-blue-600 hover:text-blue-850 hover:underline font-semibold break-all" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  {children}
                                </a>
                              )
                            }}
                          >
                            {comment.content}
                          </ReactMarkdown>
                        </div>
                      )}

                      {/* Comment attachments */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="pt-2 border-t border-gray-100 mt-2 space-y-3">
                          {/* Image preview grid */}
                          {comment.attachments.some(att => isImageFile(att.original_filename, att.mime_type)) && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                              {comment.attachments
                                .filter(att => isImageFile(att.original_filename, att.mime_type))
                                .map(img => (
                                  <a 
                                    key={img.id}
                                    href={getTaskCommentAttachmentViewUrl(img.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block border border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 transition"
                                  >
                                    <img 
                                      src={getTaskCommentAttachmentViewUrl(img.id)}
                                      alt={img.original_filename}
                                      className="object-cover w-full h-24 sm:h-28"
                                    />
                                  </a>
                                ))}
                            </div>
                          )}

                          {/* Regular files downloads list */}
                          <ul className="space-y-1 pl-1">
                            {comment.attachments.map(att => (
                              <li key={att.id} className="flex items-center text-xs text-blue-600 gap-1.5 hover:underline">
                                <FileText className="w-3.5 h-3.5 text-gray-400" />
                                <a 
                                  href={getTaskCommentAttachmentViewUrl(att.id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {att.original_filename}
                                </a>
                                <span className="text-[9px] text-gray-400">
                                  ({getFileSizeString(att.file_size)})
                                </span>
                                <a 
                                  href={getTaskCommentAttachmentDownloadUrl(att.id)}
                                  className="text-gray-400 hover:text-blue-700 font-bold ml-1"
                                  title="Скачать"
                                >
                                  ⬇
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-gray-400 italic text-sm py-2">Отчетов по этой задаче пока нет.</p>
            )}

            {/* Comment Composer */}
            <form onSubmit={handlePostCommentSubmit} className="space-y-4 border-t border-gray-150 pt-6">
              <div className="space-y-1">
                <label className="block text-xs font-bold text-gray-500 uppercase">
                  Написать отчет или комментарий
                </label>
                <MarkdownToolbar
                  textareaRef={commentTextareaRef}
                  value={commentText}
                  onChange={setCommentText}
                  users={users}
                />
                <textarea
                  ref={commentTextareaRef}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Добавьте фото/видео отчет о проделанной работе, напишите комментарий, прикрепите файлы..."
                  rows={4}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-b-lg border-t-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-sans"
                />
              </div>

              {/* Upload picker block */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => commentFileInputRef.current?.click()}
                    className="inline-flex items-center text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-250 border border-gray-250 px-3 py-2 rounded-lg transition gap-1.5"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    Прикрепить файлы / фото
                  </button>
                  <input
                    type="file"
                    ref={commentFileInputRef}
                    multiple
                    onChange={handleCommentFileChange}
                    className="hidden"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isPostingComment || (!commentText.trim() && commentFiles.length === 0)}
                  className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-sm px-4 py-2 rounded-lg shadow-sm transition gap-1.5 w-full sm:w-auto"
                >
                  <Send className="w-4 h-4" />
                  Отправить отчет
                </button>
              </div>

              {/* Local comment files queue */}
              {commentFiles.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5">
                  <div className="text-[10px] font-bold text-gray-500 uppercase">Очередь загрузки:</div>
                  <div className="flex flex-wrap gap-2">
                    {commentFiles.map((file, index) => (
                      <span key={index} className="inline-flex items-center text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 text-gray-600 gap-1.5">
                        {isImageFile(file.name) ? <Image className="w-3.5 h-3.5 text-blue-500" /> : <FileText className="w-3.5 h-3.5 text-gray-400" />}
                        <span className="truncate max-w-[150px]">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCommentFile(index)}
                          className="text-gray-400 hover:text-red-500 text-sm font-bold pl-1"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </form>
          </div>

        </div>

        {/* Right Column (Sidebar parameters card) */}
        <div className="space-y-6">
          
          {/* Status, Assignee & Deadline parameters */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
            <h3 className="font-bold text-gray-800 border-b border-gray-100 pb-2">Параметры задачи</h3>
            
            {/* Status Select */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-500 uppercase">Статус</label>
              <select
                value={task.status}
                onChange={(e) => handleStatusChange(e.target.value as TaskStatus)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold ${
                  task.status === 'completed' 
                    ? 'text-emerald-700 bg-emerald-50' 
                    : task.status === 'in_progress'
                    ? 'text-amber-700 bg-amber-50'
                    : 'text-gray-700 bg-gray-50'
                }`}
              >
                <option value="new">🆕 Новая</option>
                <option value="in_progress">⚙️ В работе</option>
                <option value="completed">✅ Выполнена</option>
              </select>
              {task.status_changer_name && (
                <div className="text-[10px] text-gray-400 italic pt-1 pl-0.5">
                  Статус изменил(а): <span className="font-semibold text-gray-500">{task.status_changer_name}</span>
                </div>
              )}
            </div>

            {/* Assignee Selection */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-500 uppercase flex items-center gap-1 mb-1">
                <User className="w-3.5 h-3.5" />
                Исполнители
              </label>
              <div className="border border-gray-300 rounded-lg p-2.5 max-h-36 overflow-y-auto space-y-1.5 bg-gray-50/30">
                {users.map((user) => {
                  const isAssigned = (task.assignees || []).some(u => u.id === user.id)
                  return (
                    <label key={user.id} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        onChange={() => handleToggleAssignee(user.id)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                      />
                      <span className={isAssigned ? 'font-bold text-blue-800' : ''}>👤 {user.name}</span>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Deadline Datepicker */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                Срок выполнения
              </label>
              <input
                type="date"
                value={task.deadline || ''}
                onChange={(e) => handleDeadlineChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                  overdue
                    ? 'border-red-300 bg-red-50 text-red-700 font-bold'
                    : 'border-gray-300'
                }`}
              />
              {overdue && (
                <p className="text-xs text-red-600 flex items-center gap-1 font-semibold">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Срок выполнения истек!
                </p>
              )}
            </div>
          </div>

          {/* Attachments Card */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
            <h3 className="font-bold text-gray-800 border-b border-gray-100 pb-2 flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-blue-500" />
              Прикрепленные файлы
            </h3>

            {/* Drag and Drop Zone */}
            <div 
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                if (e.dataTransfer.files) {
                  handleFileUpload(Array.from(e.dataTransfer.files))
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 rounded-xl p-4 text-center cursor-pointer transition"
            >
              <UploadCloud className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-xs font-semibold text-gray-600">Перетащите файлы сюда</p>
              <p className="text-[10px] text-gray-400 mt-0.5">или нажмите для обзора</p>
              <input
                type="file"
                ref={fileInputRef}
                multiple
                onChange={(e) => e.target.files && handleFileUpload(Array.from(e.target.files))}
                className="hidden"
              />
            </div>

            {isUploading && (
              <div className="text-xs text-center text-blue-600 font-semibold animate-pulse">
                Загрузка файлов...
              </div>
            )}

            {/* List of Attachments */}
            {task.attachments && task.attachments.length > 0 ? (
              <div className="space-y-2">
                {task.attachments.map((file) => (
                  <div 
                    key={file.id} 
                    className="flex items-center justify-between border border-gray-100 rounded-lg p-2 group hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="overflow-hidden">
                        <a
                          href={getTaskAttachmentViewUrl(file.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-gray-700 hover:text-blue-600 hover:underline block truncate"
                          title={file.original_filename}
                        >
                          {file.original_filename}
                        </a>
                        <span className="text-[9px] text-gray-400 block">
                          {getFileSizeString(file.file_size)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <a 
                        href={getTaskAttachmentDownloadUrl(file.id)} 
                        className="p-1 text-gray-400 hover:text-blue-600" 
                        title="Скачать файл"
                      >
                        ⬇
                      </a>
                      <button
                        onClick={() => {
                          if (confirm(`Удалить файл "${file.original_filename}"?`)) {
                            deleteAttachmentMutation.mutate(file.id)
                          }
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic text-center py-2">Нет прикрепленных файлов.</p>
            )}
          </div>

          {/* Related Tasks Card */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-blue-500" />
                Связанные задачи
              </h3>
              <button
                onClick={() => setShowRelationModal(true)}
                className="text-gray-400 hover:text-blue-600 transition"
                title="Добавить связь"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {task.relations && task.relations.length > 0 ? (
              <div className="space-y-2">
                {task.relations.map((rel) => (
                  <div 
                    key={rel.id} 
                    className="flex items-center justify-between border border-gray-100 rounded-lg p-2.5 group hover:bg-gray-50 transition"
                  >
                    <div className="overflow-hidden mr-2">
                      <Link
                        to={`/projects/${projectId}/tasks/${rel.id}`}
                        className="text-xs font-semibold text-gray-800 hover:text-blue-600 hover:underline block truncate"
                      >
                        {rel.title}
                      </Link>
                      <span className={`inline-flex items-center text-[9px] font-bold mt-0.5 ${
                        rel.status === 'completed' 
                          ? 'text-emerald-600' 
                          : rel.status === 'in_progress'
                          ? 'text-amber-600'
                          : 'text-gray-500'
                      }`}>
                        {rel.status === 'completed' ? 'Выполнена' : rel.status === 'in_progress' ? 'В работе' : 'Новая'}
                      </span>
                    </div>

                    <button
                      onClick={() => deleteRelationMutation.mutate(rel.id)}
                      className="text-gray-400 hover:text-red-600 p-1 shrink-0 opacity-0 group-hover:opacity-100 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic text-center py-2">Связей с другими задачами нет.</p>
            )}
          </div>

        </div>
      </div>

      {/* Add Relation Modal */}
      {showRelationModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowRelationModal(false)
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">🔗 Связать с задачей</h3>
              <button 
                onClick={() => setShowRelationModal(false)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold"
              >
                &times;
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-96 space-y-2">
              {potentialRelations.length > 0 ? (
                potentialRelations.map((potential) => (
                  <div
                    key={potential.id}
                    onClick={() => addRelationMutation.mutate(potential.id)}
                    className="border border-gray-200 hover:border-blue-400 hover:bg-blue-50/30 rounded-lg p-3 cursor-pointer transition flex items-center justify-between"
                  >
                    <div className="font-semibold text-sm text-gray-800">{potential.title}</div>
                    <span className="text-[10px] text-gray-400">Связать +</span>
                  </div>
                ))
              ) : (
                <p className="text-center py-6 text-xs text-gray-500">Нет доступных задач для связывания.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Subtask Modal */}
      {showAddSubtaskModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddSubtaskModal(false)
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl overflow-hidden flex flex-col my-8">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-500 animate-pulse" />
                Создать подзадачу
              </h3>
              <button 
                onClick={() => setShowAddSubtaskModal(false)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateSubtask} className="p-5 space-y-4 overflow-y-auto max-h-[70vh]">
              <div className="text-xs text-gray-500 bg-gray-50 p-2.5 rounded-lg border border-gray-150">
                Родительская задача: <strong className="text-gray-700">{task.title}</strong>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Название подзадачи <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subtaskTitle}
                  onChange={(e) => setSubtaskTitle(e.target.value)}
                  placeholder="Например: Согласовать спецификацию"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Описание подзадачи
                </label>
                <textarea
                  value={subtaskDesc}
                  onChange={(e) => setSubtaskDesc(e.target.value)}
                  placeholder="Опишите подробности выполнения подзадачи..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-sans"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Срок выполнения
                  </label>
                  <input
                    type="date"
                    value={subtaskDeadline}
                    onChange={(e) => setSubtaskDeadline(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Исполнители
                  </label>
                  <div className="border border-gray-300 rounded-lg p-2.5 max-h-32 overflow-y-auto space-y-1 bg-white">
                    {users.map((user) => (
                      <label key={user.id} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={subtaskAssignees.includes(user.id)}
                          onChange={() => {
                            setSubtaskAssignees(prev => 
                              prev.includes(user.id) 
                                ? prev.filter(id => id !== user.id) 
                                : [...prev, user.id]
                            )
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                        <span>👤 {user.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddSubtaskModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-semibold transition"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createSubtaskMutation.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-semibold transition"
                >
                  {createSubtaskMutation.isPending ? 'Создание...' : 'Создать подзадачу'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
